import React, { useEffect, useMemo, useState } from 'react';
import { SelectProps, Spin } from 'antd';
import { FetchOptionsResponse, useSelectOptions } from 'hooks/useSelectOptions';
import { debounce, uniqBy } from 'lodash';
import { SortedSelect } from 'components/ui/sorted-select';

type LazyLoadingSelectProps = SelectProps & {
  fetchOptions: (
    page: number,
    pageSize: number,
    searchText: string
  ) => Promise<FetchOptionsResponse>;
  additionalOptions?: {
    label: string | JSX.Element;
    value: string;
  }[];
  searchOptions?: {
    label: string | JSX.Element;
    value: string;
  }[];
  queryKey: string;
};

export const LazyLoadingSelect: React.FC<LazyLoadingSelectProps> = ({
  fetchOptions,
  additionalOptions,
  queryKey,
  searchOptions,
  ...selectProps
}) => {
  const [search, setSearch] = useState('');
  const { data, fetchNextPage, hasNextPage, isFetching } = useSelectOptions(
    search,
    fetchOptions,
    queryKey
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    if (
      !isFetching &&
      hasNextPage &&
      target.scrollTop + target.offsetHeight === target.scrollHeight
    ) {
      fetchNextPage();
    }
  };

  const handleBlur = (e?: React.FocusEvent<HTMLElement>) => {
    setSearch('');
    debouncedSearch.cancel();
    if (selectProps.onBlur) {
      selectProps.onBlur(e as React.FocusEvent<HTMLElement, Element>);
    }
  };

  return (
    <SortedSelect
      showSearch
      filterOption={false}
      onSearch={debouncedSearch}
      notFoundContent={isFetching ? <Spin size="small" /> : null}
      onPopupScroll={onPopupScroll}
      onBlur={handleBlur}
      className="w-full"
      options={
        searchOptions
          ? searchOptions
          : uniqBy(
              [
                ...(additionalOptions || []),
                ...(data?.pages.flatMap((page) => page.data) || []),
              ],
              'value'
            )
      }
      loading={isFetching}
      {...selectProps}
    />
  );
};
